import { createSaveQueue } from './save-queue';
import { clearSentValues } from './sent-values';
import { clearSetAsideValues } from './set-aside-values';
import type { SaveQueue, SaveQueueFlusher } from './save-queue.types';
import type { UnloadRequestBuilder } from './save-queue.types';

type Entry = {
  queue: SaveQueue;
  flusher: SaveQueueFlusher;
};

// Module scope, not React state: a queue must outlive the component that filled it,
// so navigating away mid-save cannot drop the request.
const registry = new Map<string, Entry>();

// Observers (the save-status chip) mount before the first edit creates the queue, and
// creation happens in an effect in a DESCENDANT component — which re-renders nothing
// above it. This store is the only way "a queue now exists for this key" reaches them.
const registryListeners = new Set<() => void>();
let registryVersion = 0;

const publishRegistryChange = () => {
  registryVersion += 1;

  registryListeners.forEach(listener => {
    // A broken observer must not stop the queue reaching the caller that just created it.
    try {
      listener();
    } catch (error) {
      console.error('Save queue registry subscriber threw', error);
    }
  });
};

/** Subscribes to queue creation and removal — never to a queue's own contents. */
export const subscribeToSaveQueueRegistry = (
  listener: () => void,
): (() => void) => {
  registryListeners.add(listener);
  return () => {
    registryListeners.delete(listener);
  };
};

/** Changes whenever the set of registered keys changes. Stable between changes. */
export const getSaveQueueRegistryVersion = (): number => registryVersion;

// Separate from `registry` because a form registers how to address its server as soon as
// it mounts, while its queue is not created until the first edit.
const unloadBuilders = new Map<string, UnloadRequestBuilder>();

/** Records how to turn `key`'s queued edits into unload-path writes. */
export const setUnloadRequestBuilder = (
  key: string,
  builder: UnloadRequestBuilder,
): void => {
  unloadBuilders.set(key, builder);
};

/** Removes `builder` only if it is still the registered one — a remount may have replaced it. */
export const clearUnloadRequestBuilder = (
  key: string,
  builder: UnloadRequestBuilder,
): void => {
  if (unloadBuilders.get(key) === builder) {
    unloadBuilders.delete(key);
  }
};

/** Returns the builder registered for `key`, or undefined if none is registered. */
export const getUnloadRequestBuilder = (
  key: string,
): UnloadRequestBuilder | undefined => unloadBuilders.get(key);

/** Every key with a live queue. */
export const listSaveQueueKeys = (): string[] => [...registry.keys()];

/** Returns the queue for `key`, creating it on first use and adopting the newest flusher. */
export const getSaveQueue = (
  key: string,
  flusher: SaveQueueFlusher,
): SaveQueue => {
  const existing = registry.get(key);

  if (existing) {
    existing.flusher = flusher;
    return existing.queue;
  }

  const entry: Entry = {
    flusher,
    queue: undefined as unknown as SaveQueue,
  };

  entry.queue = createSaveQueue(changes => entry.flusher(changes));
  registry.set(key, entry);
  publishRegistryChange();

  return entry.queue;
};

/**
 * Returns the queue for `key` if one is already registered, without creating one
 * or touching its flusher. Observers (status indicators) must use this — calling
 * `getSaveQueue` would overwrite the real flusher with whatever they passed.
 */
export const peekSaveQueue = (key: string): SaveQueue | undefined =>
  registry.get(key)?.queue;

export const flushAllSaveQueues = async (): Promise<void> => {
  await Promise.all([...registry.values()].map(entry => entry.queue.flush()));
};

/** Test-only: clears every registered queue, unload builder, sent and set-aside value. */
export const resetSaveQueues = (): void => {
  registry.clear();
  unloadBuilders.clear();
  clearSentValues();
  clearSetAsideValues();
  publishRegistryChange();
};
